import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Network, PanelRightOpen, Pencil, Pin, PinOff, Workflow } from 'lucide-react'
import type { FdeCopy, ModelElement, ProcessMap } from '@/frontend/features/Fde/types'

interface Props {
  element: ModelElement
  processMap: ProcessMap | null
  x: number
  y: number
  highlightPinned: boolean
  canManage: boolean
  copy: FdeCopy
  onToggleHighlight: () => void
  onShowRelated: () => void
  onOpenDetails: () => void
  onEdit: () => void
  onOpenProcess: () => void
  onClose: () => void
}

export default function OntologyNodeContextMenu(props: Props) {
  useEffect(() => {
    function closeOutside(event: PointerEvent) {
      if (event.target instanceof Element && event.target.closest('[data-ontology-context-menu]')) return
      props.onClose()
    }
    function closeFromKeyboard(event: KeyboardEvent) {
      if (event.key === 'Escape') props.onClose()
    }
    window.addEventListener('pointerdown', closeOutside)
    window.addEventListener('keydown', closeFromKeyboard)
    window.addEventListener('resize', props.onClose)
    return () => {
      window.removeEventListener('pointerdown', closeOutside)
      window.removeEventListener('keydown', closeFromKeyboard)
      window.removeEventListener('resize', props.onClose)
    }
  }, [props.onClose])

  return createPortal(
    <div
      role="menu"
      aria-label={props.copy.workspace.graph_node_actions}
      data-ontology-context-menu
      data-testid="ontology-node-context-menu"
      className="fixed z-[100] w-52 rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-xl animate-in fade-in-0 zoom-in-95"
      style={{ left: props.x, top: props.y }}
    >
      <MenuItem icon={props.highlightPinned ? PinOff : Pin} label={props.highlightPinned ? props.copy.workspace.graph_unpin_highlight : props.copy.workspace.graph_pin_highlight} onSelect={props.onToggleHighlight} />
      <MenuItem icon={Network} label={props.copy.workspace.graph_show_related} onSelect={props.onShowRelated} />
      <MenuItem icon={PanelRightOpen} label={props.copy.workspace.graph_open_details} onSelect={props.onOpenDetails} />
      {props.processMap && <MenuItem icon={Workflow} label={props.copy.workspace.process_open} onSelect={props.onOpenProcess} />}
      {props.canManage && <>
        <div className="my-1 h-px bg-border" />
        <MenuItem icon={Pencil} label={props.copy.common.edit} onSelect={props.onEdit} />
      </>}
    </div>,
    document.body,
  )
}

function MenuItem({ icon: Icon, label, onSelect }: { icon: typeof Pin; label: string; onSelect: () => void }) {
  return <button type="button" role="menuitem" onClick={onSelect} className="flex h-8 w-full items-center gap-2 rounded-md px-2 text-left text-xs outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground"><Icon className="size-3.5 text-muted-foreground" /><span className="truncate">{label}</span></button>
}
